import React from 'react'
import { connect } from 'react-redux'
import TetherComponent from 'react-tether'

import { Pane, Dialogs, PropertyGrid, Text } from '@leverege/ui-elements'
import { Selection } from '@leverege/ui-redux'
import { ContextMenu } from '@leverege/ui-plugin'
import { Attributes } from '@leverege/ui-attributes'
import { DateTime } from 'luxon'

import { formatDuration } from '../../../util/Format.js'
import CopyEventStartImageUrl from './actions/CopyEventStartImageUrl.js'
import CopyEventEndImageUrl from './actions/CopyEventEndImageUrl.js'
import CopyEventStartVideoUrl from './actions/CopyEventStartVideoUrl.js'
import CopyEventEndVideoUrl from './actions/CopyEventEndVideoUrl.js'
import OpenEventStartImageUrl from './actions/OpenEventStartImageUrl.js'
import OpenEventEndImageUrl from './actions/OpenEventEndImageUrl.js'
import OpenEventStartVideoUrl from './actions/OpenEventStartVideoUrl.js'
import OpenEventEndVideoUrl from './actions/OpenEventEndVideoUrl.js'
import Constants from '../../../util/Constants.js'
import GetStageFormat from '../../../util/Stage.js'
import { getVariant } from '../../../util/Variant.js'

import S from './LinearViewBlock.css'

const { BAY_TIME_ANDON_DEFAULTS, ANDON_COLORS } = Constants

const matchContext = {
  client : 'LinearViewBlock'
}

class LinearViewBlock extends React.Component {
  get actionContext() {
    const { item } = this.props
    return {
      item
    }
  }

  onContextMenu = ( evt ) => {
    const { isAdmin } = this.props
    
    if ( isAdmin ) {
      showPopup( evt, matchContext, this.actionContext )
    }
  }
  
  renderPopup = ( ref ) => {
    let content
    
    const { rollover, item } = this.props

    const isRollover = Selection.isSelected( rollover, item.id )

    if ( isRollover ) {
      const {
        stage : outerStage,
        averageWorkerCount,
        obj : { data : { currentStage : innerStage } } 
      } = item
      
      const endTime = item.startTime === item.stopTime ? Date.now() : item.stopTime
      const durationMS = endTime - item.startTime
      const currentStage = outerStage ?? innerStage
      const fakeTargetDurationMS = 3800000

      const targetDuration = formatDuration( fakeTargetDurationMS, { includeSeconds : false } ) // Todo: fill this in real
      const safeDurationMS = Number.isNaN( durationMS ) ? 0 : durationMS
      const durationFormatted = formatDuration( safeDurationMS, { includeSeconds : false } )
      const isDurationOnTarget = fakeTargetDurationMS >= safeDurationMS

      content = (
        <PropertyGrid
          labelVariant="ganttRolloverLabel|cycleDetailLabel|detailLabel"
          headerVariant="ganttRolloverHeader"
          variant="bayDetailCard|detailCard"
          className={S.propertyGrid}>
          <PropertyGrid.Header title="Stage Details" />
                    
          <PropertyGrid.Item
            stretch
            label="Stage Name">
            <Text variant="ganttRolloverValue|detailValue">{GetStageFormat( currentStage ).name}</Text>
          </PropertyGrid.Item>
          
          <PropertyGrid.Item
            stretch
            label="Start Time">
            <Text variant="ganttRolloverValue|detailValue">
              {DateTime.fromMillis( item.startTime ).toLocaleString( DateTime.DATETIME_SHORT_WITH_SECONDS )}
            </Text>
          </PropertyGrid.Item>
          
          <PropertyGrid.Item
            stretch
            label="End Time">
            <Text variant="ganttRolloverValue|detailValue">
              {DateTime.fromMillis( item.stopTime ).toLocaleString( DateTime.DATETIME_SHORT_WITH_SECONDS )}
            </Text>
          </PropertyGrid.Item>
          
          <PropertyGrid.Item
            stretch
            label="Target Duration">
            <Text variant="ganttRolloverValue|detailValue">{targetDuration}</Text>
          </PropertyGrid.Item>
          
          <PropertyGrid.Item
            stretch
            label="Stage Duration">
            <Text
              variant={`${isDurationOnTarget ? 'ganttRolloverOnTarget' : 'ganttRolloverOverTarget'}|detailValue`}>
              {durationFormatted}
            </Text>
          </PropertyGrid.Item>
          
          <PropertyGrid.Item
            stretch
            label="Average # of Workers">
            <Text variant="ganttRolloverValue|detailValue">{averageWorkerCount?.toString()}</Text>
          </PropertyGrid.Item>

        </PropertyGrid>
      )

      return (
        <Pane
          variant="linearViewBlockTooltip|dialog"
          domRef={ref}>
          {content}
        </Pane>
      )
    }

    return null
  }

  renderTarget = ( ref ) => {
    const { item, x1, x2, rollover, dispatch, isAdmin, ...rest } = this.props

    const isLift = item.type === 'carOnLift'

    const { obj, duration } = item

    const number = Attributes.get( 'bay.number', obj )
    let status = ANDON_COLORS.green

    if ( duration > BAY_TIME_ANDON_DEFAULTS.critical ) {
      status = ANDON_COLORS.red
    } else if ( duration > BAY_TIME_ANDON_DEFAULTS.warn ) {
      status = ANDON_COLORS.yellow
    }

    const variant = getVariant( 'linearViewBlock', status )

    return (
      <Pane
        onContextMenu={this.onContextMenu}
        domRef={ref}
        {...rest}
        variant={isLift ?
          `linearViewBlockLiftBay${number}|linearViewBlockLift|linearViewBlockBay${number}|linearViewBlock` :
          variant} />
    )
  }

  render() {
    const { item } = this.props

    if ( item.type === 'carOnJack' ) {
      return null
    }

    return (
      <TetherComponent
        attachment="top left"
        targetAttachment="bottom left"
        constraints={[ {
          to : 'scrollParent',
          pin : [ 'top', 'left' ]
        }, {
          to : 'window',
          pin : [ 'left', 'right' ]
        } ]}
        renderTarget={this.renderTarget}
        renderElement={this.renderPopup} />
    )
  }
}

function showPopup(
  event,
  matchContext,
  context,
  actions = [
    CopyEventStartImageUrl,
    CopyEventEndImageUrl,
    CopyEventStartVideoUrl,
    CopyEventEndVideoUrl,
    OpenEventStartImageUrl,
    OpenEventEndImageUrl,
    OpenEventStartVideoUrl,
    OpenEventEndVideoUrl
  ]
) {
  event.preventDefault()
  const position = { left : `${event.clientX}px`, top : `${event.clientY}px` }

  Dialogs.show( {
    component : ContextMenu,
    props : { position, actions, matchContext, context }
  } )
}

export default connect( ( state ) => {
  const roles = state.ui?.personas

  return {
    rollover : Selection.get( state, 'rollover' ),
    selected : Selection.get( state, 'selected' ),
    isAdmin : roles.some( role => role.name === 'default' )
  }
} )( LinearViewBlock )
